import { FormProvider, useForm } from 'react-hook-form';
import Modal from '@/components/modal/Modal.tsx';
import ModalHeader from '@/components/modal/ModalHeader.tsx';
import ModalContent from '@/components/modal/ModalContent.tsx';
import ModalFooter from '@/components/modal/ModalFooter.tsx';
import ActionButton from '@/components/buttons/ActionButton.tsx';
import { debugZodResolver } from '@/lib/debugZodResolver.ts';
import {
  insuranceSchema,
  InsuranceFormData,
} from '@/features/dashboard/insurances/schemas/insuranceSchema.ts';
import { insuranceMapper } from '@/features/dashboard/insurances/types/InsuranceWebDto.mapper.ts';
import useGetInsurance from '@/features/dashboard/insurances/hooks/useGetInsurance.ts';
import useUpdateInsurance from '@/features/dashboard/insurances/hooks/useUpdateInsurance.ts';
import InsuranceForm from '@/features/dashboard/insurances/components/InsuranceForm.tsx';
import { InsuranceFormSkeleton } from '@/features/dashboard/insurances/components/InsuranceFormSkeleton.tsx';
import TagPickerSection from '@/features/dashboard/tags/components/TagPickerSection.tsx';
import { TaggableType } from '@/features/dashboard/tags/types/TaggableType.ts';

const EditInsuranceModal = ({
  insuranceId,
  onClose,
}: {
  insuranceId: string;
  onClose: () => void;
}) => {
  const { data: insurance, isLoading } = useGetInsurance(insuranceId);
  const updateMutation = useUpdateInsurance(insuranceId);

  const methods = useForm<InsuranceFormData>({
    resolver: debugZodResolver(insuranceSchema),
    values: insurance ? insuranceMapper.fromDtoToForm(insurance) : undefined,
  });

  const { handleSubmit } = methods;

  const onSubmit = (data: InsuranceFormData) => {
    updateMutation.mutate(data, { onSuccess: () => onClose() });
  };

  return (
    <Modal onClose={onClose} isDirty={methods.formState.isDirty}>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <ModalHeader title="Edytuj ubezpieczenie" onClose={onClose} />
          <ModalContent>
            {isLoading ? (
              <InsuranceFormSkeleton />
            ) : (
              <div className="space-y-4">
                <InsuranceForm />
                <TagPickerSection
                  targetType={TaggableType.INSURANCE}
                  targetId={insuranceId}
                />
              </div>
            )}
          </ModalContent>
          <ModalFooter>
            <ActionButton
              tone="ghost"
              size="sm"
              type="button"
              onClick={onClose}
            >
              Anuluj
            </ActionButton>
            <ActionButton
              tone="accent"
              size="sm"
              type="submit"
              isLoading={updateMutation.isPending}
              disabled={isLoading}
            >
              Zapisz zmiany
            </ActionButton>
          </ModalFooter>
        </form>
      </FormProvider>
    </Modal>
  );
};

export default EditInsuranceModal;
